import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { EditorView } from '@codemirror/view';
import { tags as t } from '@lezer/highlight';

/**
 * Shared CodeMirror 6 theme for the (user) dashboard JSON editor.
 *
 * Ported from the admin dashboard's editor. Two differences for this app:
 *  1. The dashboard defines shadcn CSS vars as HSL *channels* (e.g.
 *     `--foreground: 222 84% 4.9%`), so chrome colors must be `hsl(var(--x))`,
 *     not `var(--x)`.
 *  2. The dashboard is light-primary (with an optional `.dark`), so the syntax
 *     palette uses mid-tone saturated colors that stay legible on both the light
 *     `--muted` surface and the dark one.
 */

// Syntax palette — mid-tone, legible on both light and dark muted surfaces.
const syntax = {
  key: '#2563eb', // property names — blue-600
  string: '#15803d', // string values — green-700
  number: '#b45309', // numbers — amber-700
  keyword: '#7c3aed', // true/false/null — violet-600
  punctuation: 'hsl(var(--muted-foreground))',
  invalid: 'hsl(var(--destructive))',
};

export const jsonEditorTheme = EditorView.theme({
  '&': {
    color: 'hsl(var(--foreground))',
    backgroundColor: 'hsl(var(--muted))',
    fontSize: '0.8125rem',
    borderRadius: 'var(--radius)',
  },
  '.cm-content': {
    fontFamily:
      'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
    caretColor: 'hsl(var(--foreground))',
    padding: '8px 0',
  },
  '&.cm-focused': {
    outline: '2px solid hsl(var(--ring))',
    outlineOffset: '0px',
  },
  '.cm-cursor, .cm-dropCursor': { borderLeftColor: 'hsl(var(--foreground))' },
  '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection': {
    backgroundColor: 'color-mix(in oklab, hsl(var(--primary)) 25%, transparent)',
  },
  '.cm-gutters': {
    backgroundColor: 'hsl(var(--muted))',
    color: 'hsl(var(--muted-foreground))',
    border: 'none',
    borderRight: '1px solid hsl(var(--border))',
  },
  '.cm-activeLineGutter': {
    backgroundColor: 'color-mix(in oklab, hsl(var(--foreground)) 6%, transparent)',
    color: 'hsl(var(--foreground))',
  },
  '.cm-activeLine': {
    backgroundColor: 'color-mix(in oklab, hsl(var(--foreground)) 4%, transparent)',
  },
  '.cm-foldPlaceholder': {
    backgroundColor: 'hsl(var(--secondary))',
    color: 'hsl(var(--muted-foreground))',
    border: '1px solid hsl(var(--border))',
    borderRadius: '4px',
    padding: '0 4px',
    margin: '0 2px',
  },
  '.cm-lint-marker-error': { content: 'none' },
  '.cm-tooltip': {
    backgroundColor: 'hsl(var(--popover))',
    color: 'hsl(var(--popover-foreground))',
    border: '1px solid hsl(var(--border))',
    borderRadius: 'var(--radius)',
  },
  '.cm-tooltip.cm-tooltip-lint': { padding: '4px 8px', fontSize: '0.75rem' },
  '.cm-matchingBracket, &.cm-focused .cm-matchingBracket': {
    backgroundColor: 'color-mix(in oklab, hsl(var(--primary)) 22%, transparent)',
    outline: 'none',
  },
  '.cm-scroller': {
    fontFamily:
      'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
    lineHeight: '1.5',
  },
  '.cm-placeholder': { color: 'hsl(var(--muted-foreground))' },
});

export const jsonHighlightStyle = HighlightStyle.define([
  { tag: [t.propertyName], color: syntax.key },
  { tag: [t.string], color: syntax.string },
  { tag: [t.number], color: syntax.number },
  { tag: [t.bool, t.null, t.keyword], color: syntax.keyword },
  {
    tag: [t.punctuation, t.brace, t.squareBracket, t.separator],
    color: syntax.punctuation,
  },
  { tag: [t.invalid], color: syntax.invalid },
]);

export const jsonHighlighting = syntaxHighlighting(jsonHighlightStyle);
